import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LandingGraphicPng from '@Assets/landingGraphic.png';
import Blob1 from '@Assets/blob1.svg';
import Blob2 from '@Assets/blob2.svg';
import PaperPlaneBlue from '@Assets/paper_plane_blue.webp';
import PaperPlaneGreen from '@Assets/paper_plane_green.webp';
import PaperPlanePink from '@Assets/paper_plane_pink.webp';
import PaperPlanePurple from '@Assets/paper_plane_purple.webp';
import TopPanel from '@CommonFunctionalDisplay/TopPanel';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_ComStyles.scss';
import './LandingPage.scss';

export default function LandingPage() {

    const navigate = useNavigate();
    const { activeUser, activeUserId, setActiveUser, activeUserAccessList } = useReduxActiveUser();
    const { currentOrgId } = useReduxOrg();
    const { setCurrentProject } = useReduxProject();
    const className = 'landingPage';

    useEffect(()=>{
        renderData();
    }, []);

    const renderData = ()=> {
        let projects = !!activeUserAccessList.projects ? activeUserAccessList.projects : [];
        let getProjectsInOrg = projects.filter((item)=> (item.parentId === currentOrgId && item.parentType === 'org') && item);
        let projectList = getProjectsInOrg.filter((item)=> item.visibility === 'leads');
        let activeProject = projectList.at(-1);
        if(!!activeProject) {
            setCurrentProject(activeProject);
        }
    }

    const handleGetStartedClick = () => {
        navigate('/login');
    };

    const stats = [
        { id: 1, value: '99.5%', label: 'Deliverability' },
        { id: 2, value: '120K', label: 'Messages / day' },
        { id: 3, value: '18%', label: 'Avg. reply lift' },
        { id: 4, value: '24/7', label: 'Automated follow-ups' }
    ];

    const pillars = [
        {
            id: 1,
            icon: '💬',
            title: 'WhatsApp + Email at scale',
            description: 'Send compliant broadcasts, sequences, and smart retries across channels with one brief.'
        },
        {
            id: 2,
            icon: '🧭',
            title: 'Lead journey control',
            description: 'Segment, score, and nudge every contact with rule-based journeys and timed outreach.'
        },
        {
            id: 3,
            icon: '📊',
            title: 'Insightful reporting',
            description: 'See sends, opens, clicks, and replies in one place with campaign and rep-level rollups.'
        }
    ];

    const channels = [
        { id: 1, label: 'WhatsApp Official API', accent: 'wa' },
        { id: 2, label: 'Email SMTP / ESP', accent: 'email' },
        { id: 3, label: 'Webhook & CRM Hooks', accent: 'web' },
        { id: 4, label: 'CSV & API Imports', accent: 'import' }
    ];

    const pipeline = [
        {
            id: 1,
            title: 'Capture',
            copy: 'Pull leads from forms, CSV, APIs, and inbound replies automatically.'
        },
        {
            id: 2,
            title: 'Nurture',
            copy: 'Schedule WhatsApp + Email cadences with branching based on engagement.'
        },
        {
            id: 3,
            title: 'Convert',
            copy: 'Hand off hot leads, log outcomes, and keep audit trails synced to your CRM.'
        }
    ];

    return (
        <div className={className}>
            <TopPanel logo={false} />
            <div className={className + '__container'}>
                <section className={className + '__hero'}>
                    <div className={className + '__hero__content'}>
                        <div className={className + '__hero__eyebrow'}>KZQ-Leads · Omni-channel outreach</div>
                        <h1 className={className + '__hero__title'}>
                            WhatsApp & Email Bulk Messaging
                            <span className={className + '__hero__title__sub'}>Built for teams that track and close leads</span>
                        </h1>
                        <p className={className + '__hero__description'}>
                            Launch compliant broadcasts, automate follow-ups, and see every touchpoint in one place. KZQ-Leads connects WhatsApp and Email so you can nurture faster and convert more predictably.
                        </p>
                        <div className={className + '__hero__actions'}>
                            <button className={className + '__cta'} onClick={handleGetStartedClick}>
                                Start sending now
                                <span className={className + '__cta__arrow'}>→</span>
                            </button>
                            <div className={className + '__hero__assurance'}>No dev work · Role-based access · Audit ready</div>
                        </div>
                        <div className={className + '__hero__stats'}>
                            {stats.map((stat) => (
                                <div key={stat.id} className={className + '__hero__stat-card'}>
                                    <div className={className + '__hero__stat-value'}>{stat.value}</div>
                                    <div className={className + '__hero__stat-label'}>{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={className + '__hero__visual'}>
                        <div className={className + '__hero__visual__glow'}>
                            <img src={Blob1} alt='' aria-hidden className={className + '__hero__visual__blob1'} />
                            <img src={Blob2} alt='' aria-hidden className={className + '__hero__visual__blob2'} />
                        </div>
                        <img src={LandingGraphicPng} alt='KZQ-Leads workspace preview' className={className + '__hero__visual__image'} />
                        <div className={className + '__hero__floating'}>
                            <img src={PaperPlaneBlue} alt='' aria-hidden className={className + '__hero__plane ' + className + '__hero__plane--1'} />
                            <img src={PaperPlaneGreen} alt='' aria-hidden className={className + '__hero__plane ' + className + '__hero__plane--2'} />
                            <img src={PaperPlanePink} alt='' aria-hidden className={className + '__hero__plane ' + className + '__hero__plane--3'} />
                            <img src={PaperPlanePurple} alt='' aria-hidden className={className + '__hero__plane ' + className + '__hero__plane--4'} />
                        </div>
                    </div>
                </section>

                <section className={className + '__pillars'}>
                    {pillars.map((pillar) => (
                        <div key={pillar.id} className={className + '__pillars__card'}>
                            <div className={className + '__pillars__icon'}>{pillar.icon}</div>
                            <h3 className={className + '__pillars__title'}>{pillar.title}</h3>
                            <p className={className + '__pillars__description'}>{pillar.description}</p>
                        </div>
                    ))}
                </section>

                <section className={className + '__channels'}>
                    <div className={className + '__channels__header'}>
                        <p className={className + '__channels__eyebrow'}>Channels & data</p>
                        <h2 className={className + '__channels__title'}>Plug into what you already use</h2>
                        <p className={className + '__channels__subtitle'}>
                            Send across WhatsApp and Email, sync to your CRM, and keep data flowing with API imports and webhooks.
                        </p>
                    </div>
                    <div className={className + '__channels__grid'}>
                        {channels.map((channel) => (
                            <div key={channel.id} className={className + '__channels__chip ' + className + `__channels__chip--${channel.accent}`}>
                                {channel.label}
                            </div>
                        ))}
                    </div>
                </section>

                <section className={className + '__pipeline'}>
                    <div className={className + '__pipeline__header'}>
                        <p className={className + '__pipeline__eyebrow'}>Lead flow</p>
                        <h2 className={className + '__pipeline__title'}>From capture to conversion</h2>
                        <p className={className + '__pipeline__subtitle'}>Operational guardrails so your team can move fast without losing compliance or context.</p>
                    </div>
                    <div className={className + '__pipeline__steps'}>
                        {pipeline.map((step, index) => (
                            <div key={step.id} className={className + '__pipeline__step'}>
                                <div className={className + '__pipeline__step__index'}>0{index + 1}</div>
                                <div className={className + '__pipeline__step__body'}>
                                    <h3>{step.title}</h3>
                                    <p>{step.copy}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className={className + '__cta-band'}>
                    <div className={className + '__cta-band__text'}>
                        <p className={className + '__cta-band__eyebrow'}>Ready to launch?</p>
                        <h3 className={className + '__cta-band__title'}>Spin up your next WhatsApp + Email campaign in minutes.</h3>
                        <p className={className + '__cta-band__subtitle'}>Role-based access, unified audit trail, and real-time engagement dashboards included.</p>
                    </div>
                    <button className={className + '__cta-band__button'} onClick={handleGetStartedClick}>
                        Go to Campaigns
                        <span className={className + '__cta-band__arrow'}>→</span>
                    </button>
                </section>
            </div>
        </div>
    );
}
